// Org admin: configure customer-provided cloud storage (S3, GCS, Azure Blob, etc.)
import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAppStore } from '@/store/app';
import apiClient from '@/api';

type ProviderId = 'aws_s3' | 'gcp_cloud_storage' | 'azure_blob' | 'digitalocean_spaces' | 'backblaze_b2' | 'minio';

interface Provider {
  id: ProviderId;
  name: string;
  description: string;
  icon?: string;
}

interface StorageConfig {
  useOwnStorage: boolean;
  provider: ProviderId | null;
  config: Record<string, any>;
  credentials: Record<string, string | null>;
  validationStatus: { isValid?: boolean; lastValidated?: string | null; lastError?: string | null };
  fallbackToDefault: boolean;
}

const blank = (): StorageConfig => ({
  useOwnStorage: false,
  provider: null,
  config: {},
  credentials: {},
  validationStatus: {},
  fallbackToDefault: true
});

// Per-provider field schema. The backend stores everything in a flexible
// `config` (non-secret) and `credentials` (encrypted) split — this drives the
// rendered form.
const FIELD_SCHEMAS: Record<ProviderId, {
  config: Array<{ key: string; label: string; placeholder?: string; required?: boolean }>;
  credentials: Array<{ key: string; label: string; placeholder?: string; required?: boolean; multiline?: boolean }>;
}> = {
  aws_s3: {
    config: [
      { key: 'region', label: 'Region', placeholder: 'ap-south-1', required: true },
      { key: 'bucket', label: 'Bucket Name', placeholder: 'my-org-docs', required: true },
      { key: 'pathPrefix', label: 'Path Prefix (optional)', placeholder: 'workidoc/' }
    ],
    credentials: [
      { key: 'accessKeyId', label: 'Access Key ID', required: true },
      { key: 'secretAccessKey', label: 'Secret Access Key', required: true }
    ]
  },
  digitalocean_spaces: {
    config: [
      { key: 'region', label: 'Region', placeholder: 'blr1', required: true },
      { key: 'bucket', label: 'Space Name', required: true },
      { key: 'endpoint', label: 'Endpoint', placeholder: 'https://blr1.digitaloceanspaces.com', required: true },
      { key: 'pathPrefix', label: 'Path Prefix (optional)' }
    ],
    credentials: [
      { key: 'accessKeyId', label: 'Access Key', required: true },
      { key: 'secretAccessKey', label: 'Secret Key', required: true }
    ]
  },
  backblaze_b2: {
    config: [
      { key: 'region', label: 'Region', placeholder: 'us-west-002', required: true },
      { key: 'bucket', label: 'Bucket Name', required: true },
      { key: 'endpoint', label: 'Endpoint', placeholder: 'https://s3.us-west-002.backblazeb2.com', required: true },
      { key: 'pathPrefix', label: 'Path Prefix (optional)' }
    ],
    credentials: [
      { key: 'accessKeyId', label: 'Key ID', required: true },
      { key: 'secretAccessKey', label: 'Application Key', required: true }
    ]
  },
  minio: {
    config: [
      { key: 'endpoint', label: 'Endpoint URL', placeholder: 'https://minio.example.com', required: true },
      { key: 'region', label: 'Region', placeholder: 'us-east-1' },
      { key: 'bucket', label: 'Bucket', required: true },
      { key: 'pathPrefix', label: 'Path Prefix (optional)' }
    ],
    credentials: [
      { key: 'accessKeyId', label: 'Access Key', required: true },
      { key: 'secretAccessKey', label: 'Secret Key', required: true }
    ]
  },
  gcp_cloud_storage: {
    config: [
      { key: 'projectId', label: 'GCP Project ID', required: true },
      { key: 'bucket', label: 'Bucket Name', required: true },
      { key: 'pathPrefix', label: 'Path Prefix (optional)' }
    ],
    credentials: [
      { key: 'serviceAccountKey', label: 'Service Account JSON Key', required: true, multiline: true,
        placeholder: 'Paste the entire service-account JSON file contents here' }
    ]
  },
  azure_blob: {
    config: [
      { key: 'accountName', label: 'Storage Account Name', required: true },
      { key: 'containerName', label: 'Container Name', required: true },
      { key: 'pathPrefix', label: 'Path Prefix (optional)' }
    ],
    credentials: [
      { key: 'connectionString', label: 'Connection String', multiline: true,
        placeholder: 'DefaultEndpointsProtocol=https;AccountName=...;AccountKey=...' },
      { key: 'sasToken', label: 'SAS Token (alternative to connection string)', multiline: true }
    ]
  }
};

const MASK_PLACEHOLDER = '****configured****';

export const StorageSettings: React.FC = () => {
  const { addNotification } = useAppStore();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [config, setConfig] = useState<StorageConfig>(blank());
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, cRes] = await Promise.all([
        apiClient.orgSettings.listStorageProviders(),
        apiClient.orgSettings.getStorage()
      ]);
      // Array-data responses are NOT auto-unwrapped (the api client only unwraps
      // when data is a non-array object), so providers come in as {data: [...]}.
      setProviders((pRes as any)?.data || (Array.isArray(pRes) ? pRes : []));
      // Object-data responses ARE auto-unwrapped, so cRes IS the storage config
      // directly. Fall back to `.data` only if some older API client version
      // doesn't unwrap.
      const cfg: StorageConfig = ((cRes as any)?.useOwnStorage !== undefined ? cRes : (cRes as any)?.data) || blank();
      setConfig({ ...blank(), ...cfg });
      // Don't seed creds with the masked placeholder values from the server —
      // we keep the form blank so the user only types what they want to change.
      setCreds({});
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Load failed', message: err?.response?.data?.error || err.message });
    } finally {
      setLoading(false);
    }
  }, [addNotification]);

  useEffect(() => { load(); }, [load]);

  const schema = config.provider ? FIELD_SCHEMAS[config.provider] : null;

  const updateConfigField = (k: string, v: any) =>
    setConfig(prev => ({ ...prev, config: { ...prev.config, [k]: v } }));

  const updateCredField = (k: string, v: string) => setCreds(prev => ({ ...prev, [k]: v }));

  const onSelectProvider = (p: ProviderId | '') => {
    setConfig(prev => ({
      ...prev,
      provider: p ? p : null,
      config: {}, // reset config when provider changes — fields differ per provider
    }));
    setCreds({});
  };

  const handleSave = async () => {
    if (!config.provider && config.useOwnStorage) {
      addNotification?.({ type: 'error', title: 'Provider required', message: 'Pick a storage provider before saving.' });
      return;
    }
    setSaving(true);
    try {
      // Only send credentials the user actually typed — avoid overwriting
      // already-stored secrets with the placeholder mask.
      const payloadCreds: Record<string, string> = {};
      for (const [k, v] of Object.entries(creds)) {
        if (v && v !== MASK_PLACEHOLDER) payloadCreds[k] = v;
      }

      await apiClient.orgSettings.updateStorage({
        useOwnStorage: config.useOwnStorage,
        provider: config.provider,
        config: config.config,
        credentials: payloadCreds,
        fallbackToDefault: config.fallbackToDefault
      });
      addNotification?.({ type: 'success', title: 'Storage saved', message: 'Settings saved. Click Validate to test the connection.' });
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Save failed', message: err?.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async () => {
    setValidating(true);
    try {
      const res: any = await apiClient.orgSettings.validateStorage();
      // res may be auto-unwrapped ({isValid,error,validatedAt}) or still wrapped ({data:{...}})
      const inner = res?.isValid !== undefined ? res : res?.data;
      if (inner?.isValid) {
        addNotification?.({ type: 'success', title: 'Connection OK', message: 'Storage credentials validated successfully.' });
      } else {
        addNotification?.({ type: 'error', title: 'Validation failed', message: inner?.error || 'Unknown error' });
      }
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Validation error', message: err?.response?.data?.error || err.message });
    } finally {
      setValidating(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset to default WorkiDoc storage? This stops uploading new files to your bucket.')) return;
    try {
      await apiClient.orgSettings.resetStorage();
      addNotification?.({ type: 'success', title: 'Reset', message: 'Reverted to default storage.' });
      await load();
    } catch (err: any) {
      addNotification?.({ type: 'error', title: 'Reset failed', message: err?.response?.data?.error || err.message });
    }
  };

  if (loading) return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Cloud Storage</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            By default, WorkiDoc stores your attachments in our managed cloud. You can route storage to your own S3,
            Azure Blob, or other bucket so files never leave your cloud account.
          </p>
        </div>
        {config.validationStatus?.isValid !== undefined && (
          <span
            className={`flex-shrink-0 inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
              config.validationStatus.isValid
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
            }`}
          >
            {config.validationStatus.isValid ? 'Connected' : 'Not validated'}
          </span>
        )}
      </div>

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={config.useOwnStorage}
          onChange={(e) => setConfig(prev => ({ ...prev, useOwnStorage: e.target.checked }))}
          className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
        <span>
          <span className="block text-sm font-medium text-gray-900 dark:text-white">Use my own cloud storage</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            All new uploads will be written to the bucket configured below.
          </span>
        </span>
      </label>

      {config.useOwnStorage && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Provider</label>
            <select
              value={config.provider || ''}
              onChange={(e) => onSelectProvider(e.target.value as ProviderId | '')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              <option value="">— select —</option>
              {providers.map(p => (
                <option key={p.id} value={p.id}>{p.name} — {p.description}</option>
              ))}
            </select>
          </div>

          {schema && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Bucket configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {schema.config.map(field => (
                    <div key={field.key}>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        {field.label}{field.required && <span className="text-red-500"> *</span>}
                      </label>
                      <input
                        type="text"
                        value={config.config[field.key] || ''}
                        placeholder={field.placeholder}
                        onChange={(e) => updateConfigField(field.key, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Credentials</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Stored encrypted (AES-256-GCM). Existing values shown as <code className="bg-gray-100 dark:bg-gray-900 px-1 rounded">{MASK_PLACEHOLDER}</code>.
                  Leave blank to keep what's already saved.
                </p>
                <div className="space-y-3">
                  {schema.credentials.map(field => {
                    const existing = config.credentials?.[field.key];
                    const placeholder = existing ? MASK_PLACEHOLDER : (field.placeholder || '');
                    return (
                      <div key={field.key}>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                          {field.label}{field.required && !existing && <span className="text-red-500"> *</span>}
                        </label>
                        {field.multiline ? (
                          <textarea
                            value={creds[field.key] || ''}
                            placeholder={placeholder}
                            onChange={(e) => updateCredField(field.key, e.target.value)}
                            rows={4}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm font-mono"
                          />
                        ) : (
                          <input
                            type="password"
                            autoComplete="new-password"
                            value={creds[field.key] || ''}
                            placeholder={placeholder}
                            onChange={(e) => updateCredField(field.key, e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={config.fallbackToDefault}
              onChange={(e) => setConfig(prev => ({ ...prev, fallbackToDefault: e.target.checked }))}
              className="mt-0.5 w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900 dark:text-white">Fall back to default storage on errors</span>
              <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                If your bucket is unreachable, uploads route to WorkiDoc's default storage instead of failing. Disable for strict
                data-residency.
              </span>
            </span>
          </label>

          {config.validationStatus?.lastError && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 px-3 py-2 text-xs text-red-700 dark:text-red-300">
              <strong>Last validation error:</strong> {config.validationStatus.lastError}
            </div>
          )}
        </>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="outline" onClick={handleReset} disabled={saving || validating}>
          Reset to default
        </Button>
        <Button variant="outline" onClick={handleValidate} disabled={validating || !config.useOwnStorage}>
          {validating ? <><LoadingSpinner size="sm" /> Validating...</> : 'Validate connection'}
        </Button>
        <Button onClick={handleSave} disabled={saving}>
          {saving ? <><LoadingSpinner size="sm" /> Saving...</> : 'Save'}
        </Button>
      </div>
    </div>
  );
};
